import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GoogleLogin } from '@react-oauth/google';

export default function Login() {
  const { t } = useTranslation();
  const { login, googleLogin, user, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // When user becomes authenticated, the App.js will handle the redirect
  // This effect is just for debugging
  useEffect(() => {
    if (user || isAuthenticated) {
      console.log('User authenticated, should redirect:', user);
    }
  }, [user, isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      
      if (!result.success) {
        setError(result.error || t('login.loginFailed'));
        setLoading(false);
      } else {
        // On success, AuthContext will update and App.js will redirect
        // Reset loading state in case redirect doesn't happen immediately
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || t('login.connectionFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent mb-2">
            {t('login.title')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-teal-100">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {t('login.signIn')}
          </h2>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm text-gray-500">or continue with</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={async (credentialResponse) => {
                    setError('');
                    setLoading(true);
                    const result = await googleLogin(credentialResponse.credential);
                    if (!result.success) {
                      setError(result.error || t('login.loginFailed'));
                    }
                    setLoading(false);
                  }}
                  onError={() => {
                    setError(t('login.loginFailed'));
                  }}
                />
              </div>
              <div className="flex items-center gap-3 mt-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm text-gray-500">or sign in with password</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <Label htmlFor="username" className="text-gray-700 font-medium">
                {t('login.username')}
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('login.enterUsername')}
                required
                className="mt-1"
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t('login.password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.enterPassword')}
                required
                className="mt-1"
                disabled={loading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-lg font-bold bg-gradient-to-r from-orange-500 to-teal-500 hover:from-orange-600 hover:to-teal-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </Button>
          </form>

        </div>

      </div>
    </div>
  );
}



